// Локальное применение мутаций (оптимистичный UI и офлайн).
// Видимое состояние клиента = состояние сервера + неотправленные мутации, применённые по порядку.
import type { ItemDto, ListDto, Mutation } from "./lists.ts";

export interface ListsState {
  lists: Record<string, ListDto>;
  items: Record<string, ItemDto>;
}

export interface ApplyContext {
  userId: string;
  now: string;
}

export const emptyListsState = (): ListsState => ({ lists: {}, items: {} });

export function applyMutation(state: ListsState, m: Mutation, ctx: ApplyContext): ListsState {
  switch (m.type) {
    case "list.create": {
      if (state.lists[m.id]) return state;
      const list: ListDto = {
        id: m.id,
        name: m.name,
        kind: m.kind,
        ownerUserId: ctx.userId,
        visibility: m.visibility,
        isTemplate: m.isTemplate,
        status: "active",
        version: 0,
        updatedAt: ctx.now,
        deletedAt: null,
      };
      return { ...state, lists: { ...state.lists, [m.id]: list } };
    }
    case "list.update": {
      const list = state.lists[m.id];
      if (!list || list.deletedAt) return state;
      const { type: _type, id: _id, ...patch } = m;
      const defined = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined));
      return { ...state, lists: { ...state.lists, [m.id]: { ...list, ...defined, updatedAt: ctx.now } } };
    }
    case "list.delete": {
      const list = state.lists[m.id];
      if (!list || list.deletedAt) return state;
      const items = { ...state.items };
      for (const it of Object.values(items)) {
        if (it.listId === m.id && !it.deletedAt) items[it.id] = { ...it, deletedAt: ctx.now };
      }
      return { lists: { ...state.lists, [m.id]: { ...list, deletedAt: ctx.now, updatedAt: ctx.now } }, items };
    }
    case "list.copy": {
      const source = state.lists[m.sourceId];
      if (!source || state.lists[m.id]) return state;
      const list: ListDto = {
        ...source,
        id: m.id,
        name: m.name,
        isTemplate: m.isTemplate,
        ownerUserId: ctx.userId,
        status: "active",
        version: 0,
        updatedAt: ctx.now,
        deletedAt: null,
      };
      const items = { ...state.items };
      for (const it of liveItems(state, m.sourceId)) {
        const newId = m.itemIds[it.id];
        if (!newId) continue;
        items[newId] = {
          ...it,
          id: newId,
          listId: m.id,
          isChecked: false,
          checkedById: null,
          version: 0,
          updatedAt: ctx.now,
        };
      }
      return { lists: { ...state.lists, [m.id]: list }, items };
    }
    case "item.create": {
      if (state.items[m.id] || !state.lists[m.listId]) return state;
      const item: ItemDto = {
        id: m.id,
        listId: m.listId,
        name: m.name,
        qty: m.qty,
        unit: m.unit,
        comment: m.comment,
        photoFileId: null,
        isChecked: false,
        checkedById: null,
        position: m.position ?? nextPosition(state, m.listId),
        version: 0,
        updatedAt: ctx.now,
        deletedAt: null,
      };
      return { ...state, items: { ...state.items, [m.id]: item } };
    }
    case "item.update": {
      const item = state.items[m.id];
      if (!item || item.deletedAt) return state;
      const { type: _type, id: _id, ...patch } = m;
      const defined = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined));
      const next: ItemDto = { ...item, ...defined, updatedAt: ctx.now };
      if (m.isChecked !== undefined) next.checkedById = m.isChecked ? ctx.userId : null;
      return { ...state, items: { ...state.items, [m.id]: next } };
    }
    case "item.delete": {
      const item = state.items[m.id];
      if (!item || item.deletedAt) return state;
      return { ...state, items: { ...state.items, [m.id]: { ...item, deletedAt: ctx.now, updatedAt: ctx.now } } };
    }
  }
}

/** Принять сущность с сервера: более новая версия заменяет старую. */
export function mergeList(state: ListsState, list: ListDto): ListsState {
  const cur = state.lists[list.id];
  if (cur && cur.version > list.version) return state;
  return { ...state, lists: { ...state.lists, [list.id]: list } };
}

export function mergeItem(state: ListsState, item: ItemDto): ListsState {
  const cur = state.items[item.id];
  if (cur && cur.version > item.version) return state;
  return { ...state, items: { ...state.items, [item.id]: item } };
}

export function liveItems(state: ListsState, listId: string): ItemDto[] {
  return Object.values(state.items)
    .filter((i) => i.listId === listId && !i.deletedAt)
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

export function nextPosition(state: ListsState, listId: string): number {
  return liveItems(state, listId).reduce((max, i) => Math.max(max, i.position), -1) + 1;
}
